import './MarketplaceSettings.scss';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useStateSelector } from '../../../../store/selectors';
import {
    LoadMarketplacePricing,
    LoadMarketplaceTemplatePricing,
} from '../../../../slices/marketplace/pricing/load-marketplace-pricing.thunk';
import { FormGroup, Col, Row } from 'reactstrap';
import '@availity/yup';
import * as yup from 'yup';
import { Field, Form } from '@availity/form';
import { FormikErrors, FormikProps } from 'formik';
import Helpers from '../../../../utils/helper';
import ConfirmationDialogWithReason from '../../../../components/ConfirmationDialog/confirmation-dialog-with-reason';
import { MarketplacePricing } from '../../../../slices/marketplace/pricing/models/marketplace-pricing.model';
import { TransactionFees } from '../../../../slices/marketplace/pricing/models/transaction-fees.model';
import createNotification from '../../../../utils/createNotification';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { FeeCalculatorSettings } from '../../../../slices/marketplace/pricing/models/fee-calculator-settings.model';
import { BrokeragePricing } from '../../../../slices/marketplace/pricing/models/brokerage-pricing.model';
import TransactionFeesTable from './TransactionFeesTable/TransactionFeesTable';
import { FeeRefundStatus } from '../../../../slices/marketplace/pricing/models/fee-refund-status.enum';
import TransactionFeeScheduleTable from './TransactionFeeScheduleTable/TransactionFeeScheduleTable';
import { MarketplacePercentField } from './MarketplaceSettingsFields/MarketplacePercentField';
import { MarketplaceCurrencyField } from './MarketplaceSettingsFields/MarketplaceCurrencyField';
import BuyersFeesTable from './BuyersFeesTable/BuyersFeesTable';
import { BuyersFees } from '../../../../slices/marketplace/pricing/models/buyers-fees-model';
import { LevelOfService, LevelsOfService, isSfas } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import ConsiderationSettingsTable from './ConsiderationSettingsTable/ConsiderationSettingsTable';
import { ConsiderationSettingsRow } from '../../../../slices/marketplace/pricing/models/consideration-settings.model';
import { ConsiderationTreatments } from '../../../../slices/marketplace/pricing/models/consideration-treatment.enum';

export interface MarketplaceSettingsProps {
    isTemplate: boolean;
    engagementId?: number;
    engagementLevelOfService?: LevelOfService;
    isConsiderationReadonly?: boolean;
}

const MarketplaceSettings = (props: MarketplaceSettingsProps) => {
    let modalForm = useRef<FormikProps<MarketplacePricing>>(null);
    const dispatch = useDispatch() as any;
    const axios = useStateSelector((state) => state.core.axios);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);

    const [settings, setSettings] = useState<MarketplacePricing>();
    const [formInitialValues, setFormInitialValues] =
        useState<MarketplacePricing>(undefined);
    const [validationSchema, setValidationSchema] =
        useState<yup.AnyObjectSchema>();

    useEffect(() => {
        setIsLoading(true);
        fetchPricing().finally(() => setIsLoading(false));
    }, []);

    const fetchPricing = async () => {
        const response = props.isTemplate
            ? await dispatch(LoadMarketplaceTemplatePricing())
            : await dispatch(LoadMarketplacePricing(props.engagementId));
        let pricingCopy: MarketplacePricing = JSON.parse(
            JSON.stringify(response.payload.data)
        );
        setSettings(pricingCopy);
        setFormInitialValues(pricingCopy);
    };

    useEffect(() => {
        if (settings && !validationSchema) {
            setValidationSchema(getDefaultValidationSchema());
        }
    }, [settings]);

    const getDefaultValidationSchema = () => {
        const numberIntField = yup.number().required().min(1, 'min 1');
        const numberFloatField = yup.number().required().min(0.1, 'min 0.1');
        const percentField = yup
            .string()
            .required()
            .test('len', 'Must be in 0..100', (val) => {
                return (
                    Number(val?.replace('%', '')) >= 0 &&
                    Number(val?.replace('%', '')) <= 100
                );
            });
        const creditableField = yup.number().min(0, 'Must be in 0..100').max(100, 'Must be in 0..100');
        const refundStatusField = yup
            .number()
            .required()
            .min(FeeRefundStatus.NonRefundable)
            .max(FeeRefundStatus.Partially);

        const feeCalculatorSettingsSchema = {
            innerRevenueMultiplePercent: percentField,
            innerValuePerDvmPercent: percentField,
            outerRevenueMultiplePercent: percentField,
            outerValuePerDvmPercent: percentField,
            middleOfRangeRevenueMultiple: numberFloatField,
            middleOfRangeValuePerDvm: numberIntField,
        };

        const transactionFeesSchema = {
            idNdaFirstXCount: numberIntField,
            idNdaFeeRefundable: refundStatusField,
            idNdaFeeCreditable: creditableField,
            dataNdaFirstXCount: numberIntField,
            dataNdaFeeRefundable: refundStatusField,
            dataNdaFeeCreditable: creditableField,
            financialModelFirstXCount: numberIntField,
            financialModelFeeRefundable: refundStatusField,
            financialModelFeeCreditable: creditableField,
            buyerMeetingVirtualCredit: numberIntField,
            buyerMeetingInPersonCredit: numberIntField,
            buyerMeetingRefundable: refundStatusField,
            buyerMeetingCreditable: creditableField,
            applicationFee: numberIntField,
            applicationFeePerDvm: numberIntField,
            applicationFeeRefundable: refundStatusField,
            applicationFeeCreditable: creditableField,
            lateFeeRefundable: refundStatusField,
            //remove if clarified by Matt
            tempoChangeFeeRefundable: refundStatusField,
            tempoChangeFeeCreditable: creditableField,
            //
            tailPeriodMonths: yup
                .string()
                .required()
                .test('len', 'Must be more than 0', (val) => {
                    return Number(val?.replace(' months', '')) > 0;
                }),
        };

        const buyersFeesSchema = {
            sellerIdFeePerDvm: numberIntField,
            sellerIdFeeRefundable: refundStatusField,
            pipAccessFeePerDvm: numberIntField,
            pipAccessFeeRefundable: refundStatusField,
            dfmAccessFeePerDvm: numberIntField,
            dfmAccessFeeRefundable: refundStatusField,
            sellerMeetingFeeVirtual: numberIntField,
            sellerMeetingFeeInPerson: numberIntField,
            sellerMeetingFeeRefundable: refundStatusField,
        };

        const considerationTreatmentField = yup
            .number()
            .required()
            .oneOf(ConsiderationTreatments.map((treatment) => treatment.id));

        return yup.object().shape({
            requestPreorderAgreementButtonText: yup.string().required(),
            considerationSettings: yup.array().of(
                yup.object().shape({
                    discountBrokerageTreatment: considerationTreatmentField,
                    fullServiceBrokerageTreatment:
                        considerationTreatmentField,
                    sfasTreatment: considerationTreatmentField,
                })
            ),
            discountBrokeragePricing: yup.object({
                feeCalculatorSettings: yup
                    .object()
                    .shape(feeCalculatorSettingsSchema),
                transactionFees: yup.object().shape({
                    ...transactionFeesSchema,
                    suspensionFeeRefundable: refundStatusField.nullable(),
                    suspensionFeeCreditable: creditableField.nullable(),
                }),
                transactionFeeSchedule: yup.array().of(
                    yup.object().shape({
                        revenueMax: numberIntField,
                        feePercent: percentField,
                        idNdaFeePerBuyer: numberIntField,
                        dataNdaFeePerBuyer: numberIntField,
                        financialModelFeePerBuyer: numberIntField,
                        lateFeeFirstOccurrence: numberIntField,
                        lateFeeAdditionalOccurrence: numberIntField,
                        suspensionFee: numberIntField,
                        tempoChangeFee: numberIntField,
                    })
                ),
                buyersFees: yup.object().shape(buyersFeesSchema),
            }),
            fullServiceBrokeragePricing: yup.object({
                feeCalculatorSettings: yup
                    .object()
                    .shape(feeCalculatorSettingsSchema),
                transactionFees: yup.object().shape({
                    ...transactionFeesSchema,
                    monthlyRetainerFeeRefundable: refundStatusField.nullable(),
                    monthlyRetainerFeeCreditable: creditableField.nullable(),
                    suspensionFeeRefundable: refundStatusField.nullable(),
                    suspensionFeeCreditable: creditableField.nullable(),
                }),
                transactionFeeSchedule: yup.array().of(
                    yup.object().shape({
                        revenueMax: numberIntField,
                        feePercent: percentField,
                        idNdaFeePerBuyer: numberIntField,
                        dataNdaFeePerBuyer: numberIntField,
                        financialModelFeePerBuyer: numberIntField,
                        monthlyRetainerFee: numberIntField,
                        lateFeeFirstOccurrence: numberIntField,
                        lateFeeAdditionalOccurrence: numberIntField,
                        suspensionFee: numberIntField,
                        tempoChangeFee: numberIntField,
                    })
                ),
                buyersFees: yup.object().shape(buyersFeesSchema),
            }),
        });
    };

    const updateTable = (
        brokerageType:
            | 'discountBrokeragePricing'
            | 'fullServiceBrokeragePricing'
            | 'sfasPricing'
            | undefined,
        table: 'feeCalculatorSettings' | 'transactionFees' | 'buyersFees',
        newState: FeeCalculatorSettings | TransactionFees | BuyersFees
    ) => {
        let newPricing: BrokeragePricing =
            brokerageType === 'discountBrokeragePricing'
                ? { ...settings.discountBrokeragePricing }
                : brokerageType === 'fullServiceBrokeragePricing'
                ? { ...settings.fullServiceBrokeragePricing }
                : { ...settings.sfasPricing };

        switch (table) {
            case 'feeCalculatorSettings':
                newPricing.feeCalculatorSettings =
                    newState as FeeCalculatorSettings;
                break;
            case 'transactionFees':
                newPricing.transactionFees = newState as TransactionFees;
                break;
            case 'buyersFees':
                newPricing.buyersFees = newState as BuyersFees;
                break;
        }

        const newSettings: MarketplacePricing = {
            ...settings,
            [brokerageType]: newPricing,
        };
        setSettings(newSettings);
    };

    const updateConsiderationSettings = (
        newState: ConsiderationSettingsRow[]
    ) => {
        const newSettings: MarketplacePricing = {
            ...settings,
            considerationSettings: newState,
        };
        setSettings(newSettings);
    };

    const considerationSettingsColumns: LevelOfService[] = props.isTemplate
        ? LevelsOfService.map((item) => item.id)
        : [props.engagementLevelOfService];

    const initSubmit = () => {
        const formValues = modalForm.current.values;

        let discountFeeSchedule =
            formValues.discountBrokeragePricing.transactionFeeSchedule;
        discountFeeSchedule[discountFeeSchedule.length - 1].revenueMax =
            discountFeeSchedule[discountFeeSchedule.length - 2].revenueMax;
        const discountSettings = { ...settings.discountBrokeragePricing };
        discountSettings.transactionFeeSchedule = discountFeeSchedule;
        discountSettings.transactionFees =
            formValues.discountBrokeragePricing.transactionFees;

        const fullServiceSettings = { ...settings.fullServiceBrokeragePricing };
        let fsFeeSchedule =
            formValues.fullServiceBrokeragePricing.transactionFeeSchedule;
        fsFeeSchedule[fsFeeSchedule.length - 1].revenueMax =
            fsFeeSchedule[fsFeeSchedule.length - 2].revenueMax;
        fullServiceSettings.transactionFeeSchedule = fsFeeSchedule;
        fullServiceSettings.transactionFees =
            formValues.fullServiceBrokeragePricing.transactionFees;

        const sfasSettings = { ...settings.sfasPricing };
        let sfasFeeSchedule =
            formValues.sfasPricing.transactionFeeSchedule;
        sfasFeeSchedule[sfasFeeSchedule.length - 1].revenueMax =
            sfasFeeSchedule[sfasFeeSchedule.length - 2].revenueMax;
        sfasSettings.transactionFeeSchedule = sfasFeeSchedule;
        sfasSettings.transactionFees = formValues.sfasPricing.transactionFees;

        const newSettings: MarketplacePricing = {
            ...settings,
            discountBrokeragePricing: discountSettings,
            fullServiceBrokeragePricing: fullServiceSettings,
            sfasPricing: sfasSettings,
        };
        setSettings(newSettings);
        modalForm.current.handleSubmit();
    };

    const saveChanges = () => {
        setIsSaving(true);
        axios
            .post('api/marketplace/pricing', settings)
            .then(async (response: any) => {
                if (response.response?.status === 403) {
                    createNotification(
                        `Marketplace Settings update failed due to concurrent updates. Please refresh page and try again.`,
                        'error'
                    );
                } else if (response.status === 200) {
                    createNotification(
                        'Marketplace Settings has been successfully updated.',
                        'success'
                    );
                    fetchPricing();
                } else {
                    createNotification(
                        `Marketplace Settings update failed due to unexpected error Please refresh page and try again.`,
                        'error'
                    );
                }
            })
            .catch(() => {
                createNotification(
                    `Marketplace Settings update failed due to unexpected error Please refresh page and try again.`,
                    'error'
                );
            })
            .finally(() => setIsSaving(false));
    };

    const updateTransactionFeeScheduleState = (
        target: string,
        rowAddedIndex: number,
        rowDeletedIndex: number,
        values: any
    ) => {
        const form = modalForm as any;
        if (
            (target && !form.current.touched[target]?.transactionFeeSchedule) ||
            (!rowAddedIndex && !rowDeletedIndex)
        )
            return;
        let touchedState = JSON.parse(
            JSON.stringify(form.current.touched[target])
        );
        if (rowDeletedIndex) {
            touchedState.transactionFeeSchedule.splice(rowDeletedIndex, 1);
            modalForm.current.setTouched(
                Object.assign(modalForm.current.touched, {
                    [target]: touchedState,
                })
            );
        } else if (
            rowAddedIndex &&
            values[target].transactionFeeSchedule.length - 1 > rowAddedIndex
        ) {
            let newRowTouchedState =
                touchedState.transactionFeeSchedule.length > rowAddedIndex
                    ? touchedState.transactionFeeSchedule[rowAddedIndex]
                    : null;

            touchedState.transactionFeeSchedule.splice(
                rowAddedIndex + 1,
                0,
                newRowTouchedState
            );
            touchedState.transactionFeeSchedule[rowAddedIndex] = null;
            modalForm.current.setTouched(
                Object.assign(modalForm.current.touched, {
                    [target]: touchedState,
                })
            );
        }
    };

    return (
        <div
            className={
                props.isTemplate
                    ? 'marketplace-settings mt15'
                    : 'marketplace-settings body_inner_content'
            }>
            <div>
                <div className="mb-3">
                    <strong>Marketplace Settings</strong>
                </div>
                <div
                    role="tabpanel"
                    id="panel-0"
                    aria-labelledby="tab-0"
                    aria-hidden="false">
                    <div className="card-body">
                        {!isLoading && settings ? (
                            <div>
                                <Form
                                    onSubmit={() => {
                                        setIsConfirmationVisible(true);
                                    }}
                                    initialValues={formInitialValues}
                                    validationSchema={validationSchema}
                                    innerRef={modalForm}>
                                    {({ values, setFieldValue }) => {
                                        return (
                                            <>
                                                <div className="body_inner_content form-content">
                                                    {props.isTemplate && (
                                                        <FormGroup>
                                                            <Row>
                                                                <Col md={2}>
                                                                    <label>
                                                                        Request
                                                                        Pre-Order
                                                                        Engagement
                                                                        Agreement
                                                                        button
                                                                        text
                                                                    </label>
                                                                </Col>
                                                                <Col
                                                                    md={8}
                                                                    className="right-padding-5">
                                                                    <Field
                                                                        type="text"
                                                                        name="requestPreorderAgreementButtonText"
                                                                        value={
                                                                            settings.requestPreorderAgreementButtonText
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) => {
                                                                            const val =
                                                                                event
                                                                                    .target
                                                                                    .value;

                                                                            const newSettings =
                                                                                {
                                                                                    ...settings,
                                                                                    requestPreorderAgreementButtonText:
                                                                                        val,
                                                                                };

                                                                            setSettings(
                                                                                newSettings
                                                                            );
                                                                        }}
                                                                    />
                                                                </Col>
                                                            </Row>
                                                        </FormGroup>
                                                    )}

                                                    {(props.isTemplate ||
                                                        !isSfas(
                                                            props.engagementLevelOfService
                                                        )) && (
                                                    <div className="transaction-fee-schedule-subsection">
                                                        <div>
                                                            <h6>
                                                                Transaction Fee
                                                                Schedule
                                                            </h6>
                                                        </div>
                                                        <div className="fee-section">
                                                            {(props.isTemplate ||
                                                                props.engagementLevelOfService ===
                                                                    LevelOfService.DiscountBrokerage) && (
                                                                <TransactionFeeScheduleTable
                                                                    pricing={values}
                                                                    brokerageType="discountBrokeragePricing"
                                                                    label="Discount Brokerage"
                                                                    setValue={(
                                                                        field: string,
                                                                        value: any,
                                                                        shouldValidate?: boolean,
                                                                        rowAddedIndex?: number,
                                                                        rowDeletedIndex?: number
                                                                    ): Promise<void | FormikErrors<MarketplacePricing>> => {
                                                                        const target =
                                                                            field.split(
                                                                                '.'
                                                                            )[0];
                                                                        return setFieldValue(
                                                                            field,
                                                                            value,
                                                                            shouldValidate
                                                                        ).then(
                                                                            () => {
                                                                                updateTransactionFeeScheduleState(
                                                                                    target,
                                                                                    rowAddedIndex,
                                                                                    rowDeletedIndex,
                                                                                    values
                                                                                );
                                                                            }
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                            {(props.isTemplate ||
                                                                props.engagementLevelOfService ===
                                                                    LevelOfService.FullServiceBrokerage) && (
                                                                <TransactionFeeScheduleTable
                                                                    pricing={values}
                                                                    brokerageType="fullServiceBrokeragePricing"
                                                                    label="Full-Service Brokerage"
                                                                    setValue={(
                                                                        field: string,
                                                                        value: any,
                                                                        shouldValidate?: boolean,
                                                                        rowAddedIndex?: number,
                                                                        rowDeletedIndex?: number
                                                                    ): Promise<void | FormikErrors<MarketplacePricing>> => {
                                                                        const target =
                                                                            field.split(
                                                                                '.'
                                                                            )[0];
                                                                        return setFieldValue(
                                                                            field,
                                                                            value,
                                                                            shouldValidate
                                                                        ).then(
                                                                            () => {
                                                                                updateTransactionFeeScheduleState(
                                                                                    target,
                                                                                    rowAddedIndex,
                                                                                    rowDeletedIndex,
                                                                                    values
                                                                                );
                                                                            }
                                                                        );
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    </div>
                                                    )}

                                                    {props.isTemplate && (
                                                    <>
                                                    <div>
                                                        <h6>
                                                            Fee Calculator
                                                            Settings
                                                        </h6>
                                                    </div>
                                                    <div className="fee-section">
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.DiscountBrokerage) && (
                                                            <>
                                                                <FormGroup>
                                                                    <Row className="center-items">
                                                                        <Col
                                                                            md={
                                                                                2
                                                                            }>
                                                                            {' '}
                                                                            <div>
                                                                                <label className="bold-label">
                                                                                    Discount
                                                                                    Brokerage
                                                                                </label>
                                                                            </div>
                                                                        </Col>
                                                                        <Col
                                                                            md={
                                                                                6
                                                                            }>
                                                                            <div className="fee-section">
                                                                                <Row className="d-none d-md-flex">
                                                                                    <Col
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        className="conditional-text-center">
                                                                                        <label>
                                                                                            Outer
                                                                                            Bound
                                                                                        </label>
                                                                                    </Col>
                                                                                    <Col
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        className="conditional-text-center">
                                                                                        <label>
                                                                                            Inner
                                                                                            Bound
                                                                                        </label>
                                                                                    </Col>
                                                                                    <Col
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        className="conditional-text-center">
                                                                                        <label>
                                                                                            Middle
                                                                                            of
                                                                                            range
                                                                                        </label>
                                                                                    </Col>
                                                                                </Row>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                </FormGroup>
                                                                <div className="fee-section">
                                                                    <FormGroup>
                                                                        <Row className="center-items margin-bottom-1-rem">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Revenue
                                                                                    Multiple
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Outer Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Inner Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Middle of range
                                                                                </label>
                                                                                <Field
                                                                                    type="number"
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple"
                                                                                    value={
                                                                                        settings
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeRevenueMultiple
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) => {
                                                                                        let value =
                                                                                            Number(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            );

                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple =
                                                                                            value;
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                        <Row className="center-items">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Value
                                                                                    per
                                                                                    DVM
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Outer Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Inner Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Middle of range
                                                                                </label>
                                                                                <MarketplaceCurrencyField
                                                                                    name="discountBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm"
                                                                                    value={
                                                                                        values
                                                                                            .discountBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeValuePerDvm
                                                                                    }
                                                                                    setFormValue={(
                                                                                        name: string,
                                                                                        value: string
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );

                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.discountBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            {
                                                                                                ...newSettings,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                    </FormGroup>
                                                                </div>
                                                            </>
                                                        )}
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.FullServiceBrokerage) && (
                                                            <>
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Full
                                                                        Service
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <div className="fee-section">
                                                                    <FormGroup>
                                                                        <Row className="center-items margin-bottom-1-rem">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Revenue
                                                                                    Multiple
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Outer Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.outerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Inner Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerRevenueMultiplePercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.innerRevenueMultiplePercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Middle of range
                                                                                </label>
                                                                                <Field
                                                                                    type="number"
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple"
                                                                                    value={
                                                                                        settings
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeRevenueMultiple
                                                                                    }
                                                                                    onChange={(
                                                                                        event
                                                                                    ) => {
                                                                                        let value =
                                                                                            Number(
                                                                                                event
                                                                                                    .target
                                                                                                    .value
                                                                                            );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeRevenueMultiple =
                                                                                            value;
                                                                                        setSettings(
                                                                                            {
                                                                                                ...newSettings,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                        <Row className="center-items">
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }>
                                                                                <label>
                                                                                    Value
                                                                                    per
                                                                                    DVM
                                                                                </label>
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Outer Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .outerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.outerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Inner Bound
                                                                                </label>
                                                                                <MarketplacePercentField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .innerValuePerDvmPercent
                                                                                    }
                                                                                    setValue={(
                                                                                        name: string,
                                                                                        value:
                                                                                            | string
                                                                                            | number
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );
                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.innerValuePerDvmPercent =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            newSettings
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                            <Col
                                                                                md={
                                                                                    2
                                                                                }
                                                                                className="right-padding-5 fee-calculator-cell">
                                                                                <label className="d-md-none fee-calculator-cell-label">
                                                                                    Middle of range
                                                                                </label>
                                                                                <MarketplaceCurrencyField
                                                                                    name="fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm"
                                                                                    value={
                                                                                        values
                                                                                            .fullServiceBrokeragePricing
                                                                                            .feeCalculatorSettings
                                                                                            .middleOfRangeValuePerDvm
                                                                                    }
                                                                                    setFormValue={(
                                                                                        name: string,
                                                                                        value: string
                                                                                    ): void => {
                                                                                        setFieldValue(
                                                                                            name,
                                                                                            value
                                                                                        );

                                                                                        const newSettings =
                                                                                            {
                                                                                                ...settings,
                                                                                            };
                                                                                        newSettings.fullServiceBrokeragePricing.feeCalculatorSettings.middleOfRangeValuePerDvm =
                                                                                            Number(
                                                                                                value
                                                                                            );
                                                                                        setSettings(
                                                                                            {
                                                                                                ...newSettings,
                                                                                            }
                                                                                        );
                                                                                    }}
                                                                                />
                                                                            </Col>
                                                                        </Row>
                                                                    </FormGroup>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                    </>
                                                    )}
                                                    {(props.isTemplate ||
                                                        !isSfas(
                                                            props.engagementLevelOfService
                                                        )) && (
                                                    <div className="transaction-fees-subsection">
                                                        <div className="mb-1">
                                                            <h6>
                                                                Transaction Fees
                                                            </h6>
                                                        </div>
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.DiscountBrokerage) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Discount
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <TransactionFeesTable
                                                                    brokerageType="discountBrokeragePricing"
                                                                    transactionFees={
                                                                        settings
                                                                            .discountBrokeragePricing
                                                                            .transactionFees
                                                                    }
                                                                    onChange={(
                                                                        newState: TransactionFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'discountBrokeragePricing',
                                                                            'transactionFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.FullServiceBrokerage) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Full
                                                                        Service
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <TransactionFeesTable
                                                                    brokerageType="fullServiceBrokeragePricing"
                                                                    transactionFees={
                                                                        settings
                                                                            .fullServiceBrokeragePricing
                                                                            .transactionFees
                                                                    }
                                                                    onChange={(
                                                                        newState: TransactionFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'fullServiceBrokeragePricing',
                                                                            'transactionFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                    </div>
                                                    )}
                                                    <div className="buyers-fees-subsection">
                                                        <div className="mb-1">
                                                            <h6>
                                                                Buyers' Fees
                                                            </h6>
                                                        </div>
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.DiscountBrokerage) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Discount
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <BuyersFeesTable
                                                                    brokerageType="discountBrokeragePricing"
                                                                    buyersFees={
                                                                        settings
                                                                            .discountBrokeragePricing
                                                                            .buyersFees
                                                                    }
                                                                    onChange={(
                                                                        newState: BuyersFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'discountBrokeragePricing',
                                                                            'buyersFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                        {(props.isTemplate ||
                                                            props.engagementLevelOfService ===
                                                                LevelOfService.FullServiceBrokerage) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Full
                                                                        Service
                                                                        Brokerage
                                                                    </label>
                                                                </div>
                                                                <BuyersFeesTable
                                                                    brokerageType="fullServiceBrokeragePricing"
                                                                    buyersFees={
                                                                        settings
                                                                            .fullServiceBrokeragePricing
                                                                            .buyersFees
                                                                    }
                                                                    onChange={(
                                                                        newState: BuyersFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'fullServiceBrokeragePricing',
                                                                            'buyersFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                        {(props.isTemplate ||
                                                            isSfas(props.engagementLevelOfService)) && (
                                                            <div className="fee-section">
                                                                <div>
                                                                    <label className="bold-label">
                                                                        Strategic
                                                                        Financial
                                                                        Advisory
                                                                    </label>
                                                                </div>
                                                                <BuyersFeesTable
                                                                    brokerageType="sfasPricing"
                                                                    buyersFees={
                                                                        settings
                                                                            .sfasPricing
                                                                            .buyersFees
                                                                    }
                                                                    onChange={(
                                                                        newState: BuyersFees
                                                                    ) =>
                                                                        updateTable(
                                                                            'sfasPricing',
                                                                            'buyersFees',
                                                                            newState
                                                                        )
                                                                    }
                                                                    setFormFieldValue={
                                                                        setFieldValue
                                                                    }
                                                                    formState={
                                                                        values
                                                                    }
                                                                    isReadonly={
                                                                        true
                                                                    }
                                                                />{' '}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="consideration-settings-subsection">
                                                        <div className="mb-1">
                                                            <h6>
                                                                {props.isTemplate
                                                                    ? 'Type of Consideration per Level of Service'
                                                                    : 'Type of Consideration'}
                                                            </h6>
                                                        </div>
                                                        <ConsiderationSettingsTable
                                                            considerationSettings={
                                                                settings.considerationSettings
                                                            }
                                                            columns={
                                                                considerationSettingsColumns
                                                            }
                                                            onChange={
                                                                updateConsiderationSettings
                                                            }
                                                            setFormFieldValue={
                                                                setFieldValue
                                                            }
                                                            formState={values}
                                                            isReadonly={
                                                                props.isConsiderationReadonly
                                                            }
                                                        />
                                                    </div>
                                                </div>

                                                <ButtonLoader
                                                    buttonText={'Save Updates'}
                                                    loaderButtonText={''}
                                                    disabled={false}
                                                    isLoading={isSaving}
                                                    onClick={initSubmit}
                                                    className="btn btn-primary"
                                                />
                                            </>
                                        );
                                    }}
                                </Form>
                            </div>
                        ) : (
                            Helpers.renderTableLoader()
                        )}
                    </div>
                </div>
            </div>
            {isConfirmationVisible && (
                <ConfirmationDialogWithReason
                    isVisible={isConfirmationVisible}
                    setIsVisible={setIsConfirmationVisible}
                    action={saveChanges}
                    reasonForAction={settings.reason}
                    setReasonForAction={(reason: string) => {
                        setSettings({
                            ...settings,
                            reason: reason,
                        });
                    }}
                    title={'Please specify reason for a settings change.'}
                    loader={isSaving}
                />
            )}
        </div>
    );
};

export default MarketplaceSettings;
